/*
 * This file is part of the Det Norske Teatret Nettside 2019 application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React, { useState, type ComponentProps } from 'react';
import Modal from 'components/Modal/Modal';
import Form, { type FormField, type FormState } from 'components/Form/Form';
import Loader from 'components/Loader/Loader';
import useEsroApi from 'esro/api';
import './Login.scss';

const DEFAULT_FIELDS: FormField[] = [];

const DEFAULT_LABELS = {
  newPassword: 'New password',
  newCustomer: 'New customer',
  newProfile: 'New profile',
  submit: 'Sign In',
  close: 'Close',
};

const DEFAULT_ERRORS: Record<string, string> = {
  required: 'Required {field}',
  invalid: 'Invalid {field}',
  invalidUsernamePassword: 'Invalid username or password',
};

const DEFAULT_ON_CLOSE = () => {};
const DEFAULT_ON_ERROR = () => {};
const DEFAULT_ON_SUCCESS = () => {};

export interface LoginProps extends Omit<
  ComponentProps<typeof Modal>,
  'className' | 'label' | 'children' | 'onClose'
> {
  passwordLink: string;
  registerLink: string;
  required?: string;
  fields?: FormField[];
  errors?: Record<string, string>;
  labels?: {
    newPassword: string;
    newCustomer: string;
    newProfile: string;
    submit: string;
    close: string;
  };
  onError?: (error: string) => void;
  onSuccess?: () => void;
  onClose?: () => void;
}

/**
 * This is the Login component.
 *
 * @author Thomas Sømoen <thomas@apt.no>
 */
function Login({
  passwordLink,
  registerLink,
  required = '',
  fields = DEFAULT_FIELDS,
  errors = DEFAULT_ERRORS,
  labels = DEFAULT_LABELS,
  onError = DEFAULT_ON_ERROR,
  onSuccess = DEFAULT_ON_SUCCESS,
  onClose = DEFAULT_ON_CLOSE,
  ...rest
}: LoginProps) {
  const esro = useEsroApi();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function onSubmit({ username, password }: FormState) {
    setLoading(true);

    esro!
      .signin(username as string, password as string)
      .then(() => {
        setLoading(false);
        onSuccess();
      })
      .catch(({ message }) => {
        const error =
          message === 'User/Password invalid.'
            ? 'invalidUsernamePassword'
            : message;

        setLoading(false);
        setError(error);
        onError(error);
      });
  }

  function renderError() {
    if (!error) return null;

    const message = errors[error] || error;

    return (
      <span className="error" aria-live="polite">
        {message}
      </span>
    );
  }

  function renderLoader() {
    if (!loading) return null;

    return <Loader />;
  }

  if (!esro) return null;

  return (
    <Modal className="login" label={labels.close} {...rest} onClose={onClose}>
      <h2>Logg inn</h2>
      <Form
        required={required}
        fields={fields}
        errors={errors}
        submit={labels.submit}
        onChange={() => {
          setError(null);
        }}
        onSubmit={onSubmit}
      >
        <div className="message error">{renderError()}</div>
        <a
          className="new-passsord"
          href={passwordLink}
          target="_blank"
          rel="noopener noreferrer"
        >
          {labels.newPassword}
        </a>
      </Form>
      <div className="new-customer">
        <h3>{labels.newCustomer}</h3>
        <a href={registerLink} target="_blank" rel="noopener noreferrer">
          {labels.newProfile}
        </a>
      </div>
      {renderLoader()}
    </Modal>
  );
}

export default Login;
